import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaToggleOn, FaToggleOff, FaUpload, FaUser } from "react-icons/fa";
import API from "../api";

const BACKEND = process.env.REACT_APP_API_URL || "http://localhost:5000";
const emptyForm = { name: "", role: "", emoji: "", color: "#0ea5e9", skills: "", linkedin: "", instagram: "", github: "", order: 0 };

export default function TeamPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const { data } = await API.get("/team/all");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => { setModal({ mode: "add", data: { ...emptyForm } }); setImageFile(null); setImagePreview(null); };
  const openEdit = (item) => {
    setModal({ mode: "edit", data: { ...item, skills: (item.skills || []).join(", ") } });
    setImageFile(null);
    setImagePreview(item.image ? `${BACKEND}${item.image}` : null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      const fields = ["name", "role", "emoji", "color", "linkedin", "instagram", "github", "active"];
      fields.forEach((f) => { if (modal.data[f] !== undefined) formData.append(f, modal.data[f]); });
      formData.append("order", modal.data.order || 0);
      formData.append("skills", JSON.stringify(modal.data.skills ? modal.data.skills.split(",").map(s => s.trim()).filter(Boolean) : []));
      if (imageFile) formData.append("image", imageFile);

      if (modal.mode === "add") {
        const { data } = await API.post("/team", formData, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => [...prev, data]);
      } else {
        const { data } = await API.put(`/team/${modal.data._id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => prev.map((i) => i._id === data._id ? data : i));
      }
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    await API.delete(`/team/${id}`);
    setItems((prev) => prev.filter((i) => i._id !== id));
  };

  const toggleActive = async (item) => {
    const formData = new FormData();
    formData.append("active", !item.active);
    const { data } = await API.put(`/team/${item._id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    setItems((prev) => prev.map((i) => i._id === data._id ? data : i));
  };

  const update = (key, value) => setModal((prev) => ({ ...prev, data: { ...prev.data, [key]: value } }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">Team</h1>
          <p className="text-gray-400 text-sm">{items.length} members</p>
        </div>
        <motion.button onClick={openAdd} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          className="btn-primary px-4 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center gap-2">
          <FaPlus className="text-xs" /> <span>Add Member</span>
        </motion.button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 && <div className="col-span-3 text-center text-gray-500 py-12">No members yet.</div>}
          {items.map((item) => (
            <motion.div key={item._id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className={`glass rounded-2xl p-5 border border-white/10 ${!item.active ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2"
                  style={{ borderColor: `${item.color}50` }}>
                  {item.image ? (
                    <img src={`${BACKEND}${item.image}`} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl"
                      style={{ background: `${item.color}20` }}>
                      {item.emoji || <FaUser className="text-gray-500" />}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">{item.name}</h3>
                  <p className="text-xs" style={{ color: item.color }}>{item.role}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-4">
                {(item.skills || []).map((s) => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${item.color}15`, color: item.color }}>{s}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                <button onClick={() => toggleActive(item)} className="text-lg">
                  {item.active ? <FaToggleOn className="text-green-400" /> : <FaToggleOff className="text-gray-500" />}
                </button>
                <button onClick={() => openEdit(item)}
                  className="flex-1 py-1.5 glass rounded-lg text-blue-400 text-xs font-semibold flex items-center justify-center gap-1">
                  <FaEdit /> Edit
                </button>
                <button onClick={() => handleDelete(item._id)}
                  className="flex-1 py-1.5 glass rounded-lg text-red-400 text-xs font-semibold flex items-center justify-center gap-1">
                  <FaTrash /> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setModal(null)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass rounded-2xl p-6 border border-white/10"
              style={{ background: "rgba(10,15,30,0.97)" }}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-bold text-lg">{modal.mode === "add" ? "Add" : "Edit"} Member</h2>
                <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><FaTimes /></button>
              </div>

              <div className="space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Profile Photo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/10 flex-shrink-0">
                      {imagePreview ? (
                        <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl"
                          style={{ background: `${modal.data.color}20` }}>
                          {modal.data.emoji || <FaUser className="text-gray-500 text-xl" />}
                        </div>
                      )}
                    </div>
                    <label className="flex-1 cursor-pointer">
                      <div className="glass border border-white/10 border-dashed rounded-xl px-4 py-3 text-center hover:border-blue-500 transition-colors">
                        <FaUpload className="text-gray-400 mx-auto mb-1" />
                        <p className="text-gray-400 text-xs">Click to upload photo</p>
                        <p className="text-gray-600 text-xs mt-0.5">JPG, PNG (max 5MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: "name", label: "Full Name", placeholder: "Rahul Sharma" },
                    { key: "role", label: "Role", placeholder: "CEO & Founder" },
                    { key: "emoji", label: "Emoji (fallback)", placeholder: "👨‍💼" },
                    { key: "order", label: "Display Order", type: "number" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="text-gray-400 text-xs mb-1.5 block">{f.label}</label>
                      <input type={f.type || "text"} value={modal.data[f.key] || ""}
                        onChange={(e) => update(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Accent Color</label>
                  <div className="flex gap-3 items-center">
                    <input type="color" value={modal.data.color || "#0ea5e9"}
                      onChange={(e) => update("color", e.target.value)}
                      className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent" />
                    <input type="text" value={modal.data.color || ""}
                      onChange={(e) => update("color", e.target.value)}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Skills (comma separated)</label>
                  <input type="text" value={modal.data.skills || ""}
                    onChange={(e) => update("skills", e.target.value)}
                    placeholder="React, Node.js, Flutter"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
                </div>

                {[
                  { key: "linkedin", label: "LinkedIn URL", placeholder: "https://linkedin.com/in/..." },
                  { key: "instagram", label: "Instagram URL", placeholder: "https://instagram.com/..." },
                  { key: "github", label: "GitHub URL", placeholder: "https://github.com/..." },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-gray-400 text-xs mb-1.5 block">{f.label}</label>
                    <input type="text" value={modal.data[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setModal(null)}
                  className="flex-1 py-2.5 glass rounded-xl text-gray-400 hover:text-white text-sm font-semibold">
                  Cancel
                </button>
                <motion.button onClick={handleSave} disabled={saving}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex-1 btn-primary py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                  {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Saving...</span></> : <span>Save Member</span>}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
